import { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, MenuItem, TextField } from "@mui/material";

import {
	DefaultParticipant,
	useCreateDefaultParticipantMutation,
	useUpdateDefaultParticipantMutation,
} from "@/pages/Private/redux/default-participants/default-participants.api";

import { Modal } from "@/components";

import { PermissionRoles } from "@/enum";

import {
	DefaultParticipantFormType,
	DefaultParticipantSchema,
} from "../schema/DefaultParticipants";
import {
	PARTICIPANT_PERMISSION_NONE,
	PARTICIPANT_PERMISSIONS,
} from "../../Course/components/SelectParticipantPermission";

interface DefaultParticipantModalProps {
	isOpen: boolean;
	onClose: () => void;
	participant?: DefaultParticipant | null;
	/** The company the participant belongs to. Defaults to the caller's own company. */
	companyId?: number;
}

export const DefaultParticipantModal: FunctionComponent<DefaultParticipantModalProps> = ({
	isOpen,
	onClose,
	participant,
	companyId,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`defaultParticipants.${key}`);
	const [create, { isLoading: isCreating }] = useCreateDefaultParticipantMutation();
	const [update, { isLoading: isUpdating }] = useUpdateDefaultParticipantMutation();

	const isEditing = !!participant;

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
		control,
	} = useForm<DefaultParticipantFormType>({
		defaultValues: {
			firstName: "",
			lastName: "",
			internalRoles: "",
			scenarioRoles: "",
		},
		resolver: zodResolver(DefaultParticipantSchema),
	});

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (participant) {
			const roles = (() => {
				try {
					return JSON.parse(participant.scenarioRoles as unknown as string);
				} catch {
					return participant.scenarioRoles;
				}
			})();

			reset({
				firstName: participant.firstName,
				lastName: participant.lastName,
				internalRoles: participant.internalRoles,
				scenarioRoles: Array.isArray(roles)
					? (roles[0] ?? PARTICIPANT_PERMISSION_NONE)
					: String(roles),
			});
		} else {
			reset({ firstName: "", lastName: "", internalRoles: "", scenarioRoles: "" });
		}
	}, [participant, reset, isOpen]);

	const onSubmit = async (values: DefaultParticipantFormType) => {
		try {
			const scenarioRoles = JSON.stringify([
				values.scenarioRoles || PARTICIPANT_PERMISSION_NONE,
			]) as unknown as JSON;

			if (isEditing) {
				await update({
					id: participant.id,
					companyId,
					body: {
						firstName: values.firstName,
						lastName: values.lastName,
						internalRoles: values.internalRoles,
						scenarioRoles,
					},
				}).unwrap();
			} else {
				await create({
					companyId,
					firstName: values.firstName,
					lastName: values.lastName,
					internalRoles: values.internalRoles,
					scenarioRoles,
				}).unwrap();
			}

			onClose();
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isCreating || isUpdating}
			isOpened={isOpen}
			submitButtonText={isEditing ? t("basics.save") : t("basics.create")}
			title={isEditing ? ts("editParticipant") : ts("addParticipant")}
		>
			<Box
				component="form"
				sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
				onSubmit={handleSubmit(onSubmit)}
			>
				<button style={{ display: "none" }} type="submit" />
				<Box sx={{ display: "flex", gap: 2 }}>
					<Box sx={{ flex: 1 }}>
						<TextField
							fullWidth
							error={!!errors.firstName}
							helperText={errors.firstName?.message}
							label={t("basics.firstName")}
							{...register("firstName")}
						/>
					</Box>
					<Box sx={{ flex: 1 }}>
						<TextField
							fullWidth
							error={!!errors.lastName}
							helperText={errors.lastName?.message}
							label={t("basics.lastName")}
							{...register("lastName")}
						/>
					</Box>
				</Box>
				<TextField
					fullWidth
					error={!!errors.internalRoles}
					helperText={errors.internalRoles?.message}
					label={ts("internalRole")}
					{...register("internalRoles")}
				/>
				<Controller
					control={control}
					name="scenarioRoles"
					render={({ field }) => (
						<TextField
							fullWidth
							select
							SelectProps={{
								renderValue: (selected) => {
									if (!selected || selected === PARTICIPANT_PERMISSION_NONE) {
										return t(`courses.progress.permissions.${PARTICIPANT_PERMISSION_NONE}`);
									}

									return t(`courses.progress.permissions.${selected as string}`);
								},
							}}
							error={!!errors.scenarioRoles}
							helperText={errors.scenarioRoles?.message}
							label={ts("scenarioRoles")}
							{...field}
						>
							<MenuItem value={PermissionRoles.PARTICIPANT}>
								{t(`courses.progress.permissions.${PermissionRoles.PARTICIPANT}`)}
							</MenuItem>
							{PARTICIPANT_PERMISSIONS.map((role) => (
								<MenuItem key={role} value={role}>
									{t(`courses.progress.permissions.${role}`)}
								</MenuItem>
							))}
						</TextField>
					)}
				/>{" "}
			</Box>
		</Modal>
	);
};
